import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useEffect } from 'react';

import FilterOfferByVenue from 'components/Category/FilterOffersByVenue';
import CategoryHead from 'components/Category/Head';
import CategoryHeading from 'components/Category/Heading';
import CategoryOffersOptionsBox from 'components/Category/OptionsBox';
import CategorySchema from 'components/Category/Schema';
import Layout from 'components/Layout';
import LazyHydration from 'components/LazyHydration';
import CategoryPageDescription from 'components/PageDescription';
import OffersList from 'components/Timeline/OffersList';
import DynamicallyLoadedBlogArticles from 'components/pages/OfferPages/DynamicallyLoadedBlogArticles';

import useDialog from 'hooks/useDialog';
import useOffersFiltersState from 'hooks/useOffersFiltersState';
import useOffersSort from 'hooks/useOffersSort';

import { isOfferCategorySlugValidToActivateGoogleAd } from 'lib/ads';
import { getOrderOffersGaEventAction, toHeadingTitle } from 'lib/category';
import { sendEvent } from 'lib/gtag';
import { offerQueryKeys } from 'lib/queryKeys';
import { sendSupixEvent } from 'lib/supix';

import { useUtm } from 'providers/UtmProvider';

import { getCategoryOffersList } from 'services/category';

import CategorySidebar from './Sidebar';

import CATEGORY from 'constants/category';
import OFFER from 'constants/offer';

const WishlistSuggestions = dynamic(
  () => import('components/Category/WishlistSuggestions')
);

const CategoryDialog = dynamic(() => import('components/Category/Dialog'), {
  ssr: false,
});

const ADS_LIST = [
  {
    adId: 'offer_category_page_main_content_1_320x50_468x60_728x90',
    adPath:
      '/promobit.com.br_offer_category_page/main_content_1_320x50_468x60_728x90',
    position: 8,
    isMobileOnly: false,
  },
  {
    adId: 'offer_category_page_main_content_2_320x50_468x60_728x90',
    adPath:
      '/promobit.com.br_offer_category_page/main_content_2_320x50_468x60_728x90',
    position: 19,
    isMobileOnly: true,
  },
];
const GA_EVENT_CATEGORY = 'category_page';

const onNavItemClick = (name) => {
  sendEvent({
    category: GA_EVENT_CATEGORY,
    action: 'subcategory',
    value: name,
  });
};

const onLoadMoreOffersButtonClick = () => {
  sendEvent({ category: GA_EVENT_CATEGORY, action: 'load_more' });
};

const CategoryPage = ({ category, serverOffers }) => {
  const {
    articles,
    categoryId,
    categoryName,
    categorySlug,
    subcategories,
    pageDescription,
    wishlistSuggestions = [],
  } = category;
  const { showDialog } = useDialog();
  const { options, offersSort, setOffersSort } = useOffersSort();
  const [offersFilters, setOffersFilters] = useOffersFiltersState();
  const { isUtmLoaded } = useUtm();

  useEffect(() => {
    if (isUtmLoaded) {
      sendSupixEvent('/car', {
        category: categoryId,
      });
    }
  }, [categoryId, isUtmLoaded]);

  const onOffersSortChange = ({ target: { value } }) => {
    sendEvent({
      category: GA_EVENT_CATEGORY,
      action: getOrderOffersGaEventAction(value),
    });
    setOffersSort(value);
  };

  const onOffersVenueFilterChange = (venue) => {
    setOffersFilters({ type: OFFER.FILTER_ACTION_TYPES.VENUE, venue });
  };

  const onFilterButtonClick = () => {
    showDialog(CategoryDialog, {
      categoryName: category.categoryName,
      categorySlug: category.categorySlug,
      gaEventCategory: GA_EVENT_CATEGORY,
      isSpaced: true,
      subcategories: category.subcategories,
      onNavItemClick: onNavItemClick,
    });
  };

  const queryFn = ({ after }, opts) =>
    getCategoryOffersList(
      {
        after,
        categorySlug,
        filters: offersFilters,
        sort: offersSort,
        limit: CATEGORY.OFFERS.CLIENT_LIMIT,
      },
      opts
    );

  const isGoogleAdActive = isOfferCategorySlugValidToActivateGoogleAd(
    category.categorySlug
  );

  return (
    <>
      <CategoryHead categoryName={category.categoryName} />
      <CategorySchema {...category} offers={serverOffers.offers} />
      <Layout
        headingComponent={
          <CategoryHeading
            categoryName={categoryName}
            categorySlug={categorySlug}
            title={toHeadingTitle(categoryName)}
          />
        }
        sidebarComponent={
          <CategorySidebar
            categoryName={categoryName}
            className="hidden xl:flex"
            gaEventCategory={GA_EVENT_CATEGORY}
            isGoogleAdActive={isGoogleAdActive}
            offersFilters={offersFilters}
            position="left"
            subcategories={subcategories}
            onNavItemClick={onNavItemClick}
            onOffersVenueFilterChange={onOffersVenueFilterChange}
          />
        }
      >
        <CategoryOffersOptionsBox
          offersFilters={offersFilters}
          options={options}
          onFilterButtonClick={onFilterButtonClick}
          onOffersFiltersChange={setOffersFilters}
          onOffersSortChange={onOffersSortChange}
          onOffersVenueFilterChange={onOffersVenueFilterChange}
        />
        <FilterOfferByVenue
          className="mt-4 xl:hidden"
          gaEventCategory={GA_EVENT_CATEGORY}
          venue={offersFilters.venue}
          onOffersVenueFilterChange={onOffersVenueFilterChange}
        />
        <OffersList
          cardTitleAs="h2"
          gaEventCategory={GA_EVENT_CATEGORY}
          googleAds={isGoogleAdActive ? ADS_LIST : []}
          initialOffers={serverOffers}
          offersFilters={offersFilters}
          queryFn={queryFn}
          queryKey={offerQueryKeys.listByCategory({
            categoryId,
            offersSort,
            offersFiltersVenue: offersFilters.venue,
          })}
          paginationCappedAtFinishedOffer
          showButtonBeforeFirstPagination
          onLoadMoreOffersButtonClick={onLoadMoreOffersButtonClick}
        />
        {wishlistSuggestions?.length > 0 && (
          <WishlistSuggestions
            gaEventCategory={GA_EVENT_CATEGORY}
            keywordButtonType="primary"
            keywordsDisplay={wishlistSuggestions}
            withEditWishlist
          />
        )}
        <DynamicallyLoadedBlogArticles
          articles={articles}
          className="mt-6"
          gaEventCategory={GA_EVENT_CATEGORY}
        />
        <LazyHydration placeholderSize={{ height: 300 }}>
          <CategoryPageDescription
            className="mb-12 mt-6 bg-transparent pb-6 dark:bg-transparent"
            pageDescription={pageDescription}
          />
        </LazyHydration>
      </Layout>
    </>
  );
};

CategoryPage.propTypes = {
  category: PropTypes.shape({
    articles: PropTypes.arrayOf(
      PropTypes.shape({
        articleId: PropTypes.number.isRequired,
        articleDescription: PropTypes.string.isRequired,
        articleSlug: PropTypes.string.isRequired,
        articleTitle: PropTypes.string.isRequired,
      })
    ),
    categoryId: PropTypes.number.isRequired,
    categoryName: PropTypes.string.isRequired,
    categorySlug: PropTypes.string.isRequired,
    metaDescription: PropTypes.string,
    metaTitle: PropTypes.string,
    pageDescription: PropTypes.string,
    pageTitle: PropTypes.string,
    subcategories: PropTypes.arrayOf(
      PropTypes.shape({
        subcategoryId: PropTypes.number.isRequired,
        subcategoryName: PropTypes.string.isRequired,
        subcategorySlug: PropTypes.string.isRequired,
      })
    ),
    wishlistSuggestions: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  serverOffers: PropTypes.shape({
    after: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sort: PropTypes.string,
    before: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    offers: PropTypes.arrayOf(
      PropTypes.shape({
        categoryId: PropTypes.number,
        categoryName: PropTypes.string,
        categorySlug: PropTypes.string,
        key: PropTypes.string.isRequired,
        offerComments: PropTypes.number.isRequired,
        offerId: PropTypes.number.isRequired,
        offerIsHighlight: PropTypes.bool.isRequired,
        ratings: PropTypes.shape().isRequired,
        offerOldPrice: PropTypes.number,
        offerPhoto: PropTypes.string.isRequired,
        offerPrice: PropTypes.number.isRequired,
        offerPriceType: PropTypes.string.isRequired,
        offerPublished: PropTypes.string.isRequired,
        offerSlug: PropTypes.string.isRequired,
        offerStatusName: PropTypes.string.isRequired,
        offerUserVisibility: PropTypes.string.isRequired,
        offerTags: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
          })
        ),
        offerTitle: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
        storeId: PropTypes.number,
        subcategoryId: PropTypes.number,
        subcategoryName: PropTypes.string,
        subcategorySlug: PropTypes.string,
      }).isRequired
    ).isRequired,
  }).isRequired,
};

export default CategoryPage;
